/**
 * TanStack Query hooks for static file management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staticFilesApi } from '../api/staticFiles';

// ============================================================================
// Query Keys
// ============================================================================

export const staticFileKeys = {
  all: (siteSlug: string) => ['sites', siteSlug, 'static-files'] as const,
  list: (siteSlug: string) => [...staticFileKeys.all(siteSlug), 'list'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/** Fetch static files list */
export function useStaticFiles(siteSlug: string) {
  return useQuery({
    queryKey: staticFileKeys.list(siteSlug),
    queryFn: () => staticFilesApi.list(siteSlug),
    enabled: !!siteSlug,
  });
}

/** Upload a static file */
export function useUploadStaticFile(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, subPath }: { file: File; subPath?: string }) =>
      staticFilesApi.upload(siteSlug, file, subPath),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staticFileKeys.all(siteSlug) });
    },
  });
}

/** Delete a static file */
export function useDeleteStaticFile(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (filePath: string) => staticFilesApi.delete(siteSlug, filePath),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staticFileKeys.all(siteSlug) });
    },
  });
}
